import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useParams, useSearchParams, useLoaderData, useNavigate, useLocation } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import classnames from 'classnames';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import {
  MapPin,
  Home,
  User,
  Ruler,
  DollarSign
} from 'lucide-react';

import ChatMessage, { ChatMessageProps } from './ChatMessage';
import ContractorBidSubmission from '../Contractor/ContractorBidSubmission';
import HomeownerBidList from './HomeownerBidList';
import AttachmentGallery from './AttachmentGallery';
import { formatAddress, Address } from '../../models/Address.interface';
import { useUser } from '../../hooks/GlobalUserHooks';
import CategoryBadges from './CategoryBadges';
import JobEdit from './JobEdit';
import { updateJob } from '../../slices/jobs/reducer';
import { Job } from '../../models/Job.interface';
import { LoaderData } from '../../types/LoaderData';
import './JobDetailPage.css';
import { RootState } from '../../index';
import { handleContractorAuth } from '../../slices/auth/contractor/thunk';
import { fetchJobs } from '../../slices/jobs/thunk';
import SEOTags from '../Listing/SEOTags';
import LoadingOverlay from '../../Components/LoadingOverlay';

// MIGRATION CHANGE: Import the new JobService for MySQL/Firebase integration
import { jobService } from '../../services/jobService';

// Update the JobDetail interface to ensure all properties are optional
interface JobDetail extends Partial<Job> {
  chatMessages?: ExtendedChatMessage[];
  jobDetailModified?: string;
  jobDetailModifiedHTML?: string;
  jobSummaryModified?: string;
  jobSummaryModifiedHTML?: string;
}

// Extended ChatMessage interface to handle both legacy and MySQL formats
interface ExtendedChatMessage {
  id?: string;
  message?: string;
  message_text?: string;
  role?: 'User' | 'Contractor' | 'BidBro';
  timestamp?: string;
  created_at?: string;
  displayName?: string;
  senderName?: string;
  sender_name?: string;
  sender_id?: string;
  job_id?: string;
  message_type?: string;
  name?: string;
}

const JobDetailPage = () => {
  console.log('JobDetailPage component rendering');

  // FIXED: Better parameter extraction logic
  const params = useParams();
  const location = useLocation();
  console.log('🔍 Route params received:', params);
  console.log('🔍 Current location:', location.pathname);

  // Extract parameters with proper fallbacks
  const id = params.id;
  const seo_url = params.seo_url;
  const jobid = params.jobid;

  // Check if this is a public route - if seo_url is set, show only Job Summary tab
  const isPublicRoute = location.pathname.startsWith('/job/') && seo_url && jobid;

  console.log('🔍 Extracted parameters:', { id, seo_url, jobid, isPublicRoute });

  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [activeArrowTab, setActiveArrowTab] = useState(() => {
    // Force tab 1 for public routes
    if (isPublicRoute) return 1;
    const tab = searchParams.get('tab');
    if (tab === 'bids') return 3;
    if (tab === 'changes') return 2;
    return 1;
  });

  const loaderData = useLoaderData() as LoaderData | null;
  const { seoUrl = null, contractorId: urlContractorId = null } = loaderData || {};
  const { userName, currentUserId, contractor, isContractor: userIsContractor } = useUser();
  const contractorId = urlContractorId || searchParams.get('cid') || contractor?.contractorId || null;
  const isContractor = contractorId ? true : userIsContractor;

  // Chat-related state
  const [chatMessages, setChatMessages] = useState<ExtendedChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Other state
  const [contractorBidId, setContractorBidId] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [actualJobId, setActualJobId] = useState<string | undefined>();
  const [isJobOwner, setIsJobOwner] = useState(false);

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const jobs = useSelector((state: RootState) => state.Jobs?.jobs);

  // Helper function to render text with line breaks (simplified without markdown)
  const renderTextWithLineBreaks = (text: string | undefined) => {
    if (!text) return null;

    return (
      <div>
        {text.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < text.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Property Details Component - Modern card-based layout
  const PropertyDetailsSection = ({ propertyDetails }: { propertyDetails: any }) => {
    console.log('🔍 PropertyDetailsSection called with:', propertyDetails);

    if (!propertyDetails) {
      console.log('❌ PropertyDetails is null/undefined, not rendering');
      return null;
    }

    const formatPropertyValue = (value: any, type: 'currency' | 'number' | 'text' = 'text') => {
      if (value === null || value === undefined || value === '') return 'N/A';

      switch (type) {
        case 'currency':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }).format(value);
        case 'number':
          return new Intl.NumberFormat('en-US').format(value);
        default:
          return value.toString();
      }
    };

    const propertyCards = [
      {
        title: 'Location Details',
        icon: MapPin,
        items: [
          { label: 'County', value: formatPropertyValue(propertyDetails.county) },
          { label: 'Jurisdiction', value: formatPropertyValue(propertyDetails.jurisdiction) },
          { label: 'Census Tract', value: formatPropertyValue(propertyDetails.census_tract) },
          { label: 'Congressional District', value: formatPropertyValue(propertyDetails.congressional_district) },
        ]
      },
      {
        title: 'Property Info',
        icon: Home,
        items: [
          { label: 'Property Type', value: formatPropertyValue(propertyDetails.property_type) },
          { label: 'Type Detail', value: formatPropertyValue(propertyDetails.property_type_detail) },
          { label: 'Year Built', value: formatPropertyValue(propertyDetails.year_built, 'number') },
          { label: 'Story Count', value: formatPropertyValue(propertyDetails.story_count, 'number') },
        ]
      },
      {
        title: 'Ownership',
        icon: User,
        items: [
          { label: 'Legal Owner', value: formatPropertyValue(propertyDetails.legal_owner) },
          { label: 'Owner Type', value: formatPropertyValue(propertyDetails.owner_type) },
        ]
      },
      {
        title: 'Measurements',
        icon: Ruler,
        items: [
          { label: 'Lot Size', value: `${formatPropertyValue(propertyDetails.lot_size, 'number')} sq ft` },
          { label: 'Building Area', value: `${formatPropertyValue(propertyDetails.building_area, 'number')} sq ft` },
          { label: 'Unit Count', value: formatPropertyValue(propertyDetails.unit_count, 'number') },
        ]
      },
      {
        title: 'Valuation',
        icon: DollarSign,
        items: [
          { label: 'Assessed Market Value', value: formatPropertyValue(propertyDetails.assessed_market_value, 'currency') },
        ]
      }
    ];

    return (
      <div className="property-details-modern mt-4">
        <div className="section-header mb-4">
          <h5 className="section-title">
            <i className="ri-building-line me-2"></i>
            Property Intelligence
          </h5>
          <p className="section-subtitle text-muted mb-0">Comprehensive property data and insights</p>
        </div>
        <Row className="g-3">
          {propertyCards.map((card, index) => (
            <Col key={index} lg={4} md={6} xs={12}>
              <Card className="property-card h-100 border-0 shadow-sm">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="property-icon me-2">
                      <card.icon size={18} />
                    </div>
                    <h6 className="card-title mb-0 fw-semibold">{card.title}</h6>
                  </div>
                  <div className="property-items">
                    {card.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="property-item d-flex justify-content-between align-items-center py-1">
                        <span className="item-label text-muted small">{item.label}:</span>
                        <span className="item-value fw-medium small">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Helper function to safely get HTML content
  const getSafeHtmlContent = (htmlContent?: string, fallback?: string): string => {
    if (htmlContent && htmlContent.trim() !== '') {
      return htmlContent;
    }
    return fallback || '';
  };

  // MIGRATION CHANGE: Updated fetchJobDetail to use new JobService
  const fetchJobDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let jobData = null;

      if (isPublicRoute && seo_url && jobid) {
        // For public routes, use the public API endpoint
        console.log('🔍 Fetching job via public route:', { seo_url, jobid });
        jobData = await jobService.fetchJobByUrl(seo_url, jobid);
      } else if (id || jobid) {
        // For authenticated routes, use the regular job detail endpoint
        const targetId = jobid || id;
        if (targetId) {
          console.log('🔍 Fetching job via authenticated route:', targetId);
          jobData = await jobService.fetchJob(targetId);
        }
      }

      if (jobData) {
        setJobDetail(jobData);
        setActualJobId(jobData.id);
        setChatMessages(jobData.chatMessages || []);
        console.log('✅ Job detail loaded:', jobData);
      } else {
        setError('Job not found');
      }
    } catch (error) {
      console.error('❌ Error fetching job detail:', error);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [id, seo_url, jobid, isPublicRoute]);

  const updateViewStatus = useCallback(async () => {
    if (!isContractor || !contractorId || !actualJobId) {
      return;
    }

    try {
      console.log('🔄 Updating view status...', { contractorId, actualJobId });
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/contractors/${contractorId}/jobs/${actualJobId}/view`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('✅ View status updated:', response.data);
    } catch (error) {
      console.error('❌ Failed to update view status:', error);
    }
  }, [isContractor, contractorId, actualJobId]);

  const fetchContractorBidId = useCallback(async () => {
    if (!isContractor || !contractorId || !actualJobId) {
      return;
    }

    try {
      console.log('🔄 Fetching contractor bid ID...', { contractorId, actualJobId });
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/contractors/${contractorId}/jobs/${actualJobId}/bid`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.bidId) {
        setContractorBidId(response.data.bidId);
        console.log('✅ Contractor bid ID found:', response.data.bidId);
      } else {
        console.log('ℹ️ No bid found for this contractor on this job');
      }
    } catch (error) {
      console.error('❌ Failed to fetch contractor bid ID:', error);
    }
  }, [isContractor, contractorId, actualJobId]);

  const checkContractorBid = useCallback(async () => {
    await fetchContractorBidId();
  }, [fetchContractorBidId]);

  const sendChatMessage = async (messageText: string, senderName: string) => {
    if (!actualJobId || !messageText.trim()) {
      return;
    }

    try {
      // MIGRATION CHANGE: Use new API endpoint for sending messages
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jobs/${actualJobId}/messages`,
        {
          message_text: messageText,
          sender_name: senderName,
          role: isContractor ? 'Contractor' : 'User',
          sender_id: contractorId || currentUserId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 201) {
        // Add the message to local state immediately for better UX
        const newMessage: ExtendedChatMessage = {
          id: response.data.id,
          message_text: messageText,
          sender_name: senderName,
          role: isContractor ? 'Contractor' : 'User',
          created_at: new Date().toISOString(),
          sender_id: contractorId || currentUserId,
        };
        setChatMessages(prev => [...prev, newMessage]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const senderName = isContractor ?
      (contractor?.businessName || contractor?.contactName || 'Contractor') :
      userName || 'User';

    sendChatMessage(newMessage, senderName);
  };

  const handleJobUpdate = (updatedJob: Job) => {
    setJobDetail(updatedJob);
    dispatch(updateJob(updatedJob));
    setIsEditModalOpen(false);
  };

  const handleEditToggle = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const handleBidSubmit = () => {
    // After bid submission, refresh contractor bid ID
    setTimeout(() => {
      fetchContractorBidId();
    }, 1000);
  };

  const toggleArrowTab = (tab: number) => {
    // For public routes, don't allow tab switching beyond tab 1
    if (isPublicRoute && tab !== 1) {
      return;
    }
    if (activeArrowTab !== tab) {
      setActiveArrowTab(tab);
    }
  };

  // Helper function to strip HTML tags (from previous version)
  const stripHtml = (html: string): string => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Safely handle date conversion
  const formatCreatedDate = (inputDate: any): string => {
    if (!inputDate) {
      return 'Date not available';
    }

    // Handle Firestore Timestamp
    if (inputDate && typeof inputDate === 'object' && 'seconds' in inputDate && 'nanoseconds' in inputDate) {
      try {
        // Convert Firestore Timestamp to JavaScript Date
        const date = new Date(inputDate.seconds * 1000 + inputDate.nanoseconds / 1000000);
        return date.toLocaleString();
      } catch (e) {
        console.error('Error formatting Firestore timestamp:', e);
        return 'Invalid date format';
      }
    } else if (typeof inputDate === 'string') {
      // Handle ISO string
      return new Date(inputDate).toLocaleString();
    } else if (inputDate) {
      // Handle any other format - this is a fallback
      try {
        return new Date(inputDate).toLocaleString();
      } catch (e) {
        console.error('Error formatting date:', e);
        return 'Invalid date format';
      }
    }
    return 'Date not available';
  };

  // Safely get categories or empty array
  const getCategories = (): string[] => {
    return jobDetail?.jobCategories || [];
  };

  // Safely get job detail or empty string
  const getJobDetail = (): string => {
    return jobDetail?.jobDetailModified || jobDetail?.jobDetail || '';
  };

  // Format job value for display
  const formatJobValue = (value?: number | null): string => {
    if (!value || value === 0) return '';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const handleSubmit = () => {
    handleSendMessage();
  };

  // Handle Enter key in message input
  const handleMessageKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Effects - Updated to not call document.title as we use SEOTags now
  useEffect(() => {
    console.log("JobDetailPage id from params:", id);
    console.log("JobDetailPage actualJobId:", actualJobId);
  }, [id, actualJobId]);

  // Load job details when component mounts or params change
  useEffect(() => {
    fetchJobDetail();
  }, [fetchJobDetail]);

  // Update view status for contractors
  useEffect(() => {
    if (isContractor && contractorId && actualJobId) {
      updateViewStatus();
    }
  }, [isContractor, contractorId, actualJobId, updateViewStatus]);

  // Fetch contractor bid ID
  useEffect(() => {
    if (isContractor && contractorId && actualJobId) {
      fetchContractorBidId();
    }
  }, [isContractor, contractorId, actualJobId, fetchContractorBidId]);

  // Handle contractor authentication
  useEffect(() => {
    if (contractorId) {
      dispatch(handleContractorAuth(contractorId || null) as any);
    }
  }, [dispatch, contractorId]);

  // Initialize component
  useEffect(() => {
    const initializeJobDetail = async () => {
      // Check if jobs need to be fetched for authenticated users
      if (!jobs?.length && currentUserId && !isPublicRoute) {
        dispatch(fetchJobs(currentUserId));
      }

      // Only check bids and update view status for authenticated routes
      if (!isPublicRoute) {
        await checkContractorBid();

        // Update view status if contractor is viewing
        if (contractorId) {
          await updateViewStatus();
        }
      }
    };

    if (actualJobId) {
      initializeJobDetail();
    }
  }, [actualJobId, currentUserId, jobs, isPublicRoute, contractorId, checkContractorBid, updateViewStatus, dispatch]);

  // Set job owner status
  useEffect(() => {
    setIsJobOwner(currentUserId === jobDetail?.userId);
  }, [jobDetail, currentUserId]);

  if (loading) {
    return (
       <LoadingOverlay
                isVisible={true}
                text="BidBro"
                subtext="Loading job details..."
            />
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Error</h4>
            <p>{error}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!jobDetail) {
    return <div>Job not found</div>;
  }

  // Build SEO data
  const jobAddress = formatAddress(jobDetail.propertyAddress);
  const seoTitle = jobAddress && jobDetail.jobTitle
    ? `${jobAddress} - ${jobDetail.jobTitle}`.length > 60
      ? `${jobDetail.jobTitle} - ${jobAddress}`
      : `${jobAddress} - ${jobDetail.jobTitle}`
    : jobDetail.jobTitle || 'Job Details';

  const seoDescription = [
    jobDetail.jobSummary,
    jobDetail.jobDescription,
    jobDetail.jobDetail,
    jobAddress
  ].filter(Boolean).join(' ').substring(0, 160);

  // Ensure jobAttachments is properly defined and has the expected structure
  const normalizedAttachments = Array.isArray(jobDetail?.jobAttachments)
    ? jobDetail.jobAttachments.filter(
      attachment => attachment && typeof attachment === 'object' && typeof attachment.url === 'string'
    )
    : [];

  console.log('JobDetailPage normalized attachments:', normalizedAttachments);

  return (
    <div className="page-content">
      {/* SEO Tags */}
      <SEOTags
        title={seoTitle}
        description={seoDescription}
        schemaType="WebPage"
        schemaData={{
          name: jobDetail.jobTitle,
          description: seoDescription,
          address: jobAddress,
          dateCreated: jobDetail.dtCreated,
          jobValue: jobDetail.jobValue
        }}
      />

      <Container fluid>
        {!isPublicRoute && (<BreadCrumb title="Job Details" pageTitle="Projects" />)}

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {/* Job Header with responsive layout */}
                <Row className="mb-4">
                  <Col xs={12}>
                    <div className="job-header">
                      {/* Job Title */}
                      <h4 className="job-title mb-2 fw-bold fs-4">{jobDetail.jobTitle}</h4>

                      {/* Job Value - responsive positioning */}
                      {jobDetail.jobValue && jobDetail.jobValue > 0 && (
                        <div className="job-value d-block d-md-inline-block">
                          <span className="badge bg-success fs-6 px-3 py-2">
                            {formatJobValue(jobDetail.jobValue)}
                          </span>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Only show edit button and tabs for authenticated routes */}
                {!isPublicRoute && (
                  <>
                    {/* Edit Button */}
                    {isJobOwner && (
                      <Row className="mb-3">
                        <Col>
                          <Button
                            color="primary"
                            onClick={handleEditToggle}
                            className="btn-sm"
                          >
                            <i className="ri-edit-line align-bottom me-1"></i>
                            Edit Job
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </>
                )}

                <Row>
                  <Col xl={12}>
                    <div className="form-wizard">
                      {/* Tab Navigation - matches Release 1.5.0 styling */}
                      <Nav
                        className="nav nav-pills custom-nav nav-justified"
                        role="tablist"
                      >
                        {/* Tab 1: Job Summary - Always visible, but hide button text on public routes */}
                        <NavItem>
                          <NavLink
                            href="#"
                            id="steparrow-job-summary-tab"
                            className={classnames({ active: activeArrowTab === 1 })}
                            onClick={() => toggleArrowTab(1)}
                            style={isPublicRoute ? { display: 'none' } : {}}
                          >
                            Job Summary
                          </NavLink>
                        </NavItem>

                        {/* Tab 2: Job Changes & Inquiries - Hidden on public routes */}
                        {!isPublicRoute && (isJobOwner || contractor) && (
                          <NavItem>
                            <NavLink
                              href="#"
                              id="steparrow-job-changes-tab"
                              className={classnames({ active: activeArrowTab === 2 })}
                              onClick={() => toggleArrowTab(2)}
                            >
                              Job Changes & Inquiries
                            </NavLink>
                          </NavItem>
                        )}

                        {/* Tab 3: Bids/Hiring - Hidden on public routes */}
                        {!isPublicRoute && (isJobOwner || isContractor) && (
                          <NavItem>
                            <NavLink
                              href="#"
                              id="steparrow-job-bids-tab"
                              className={classnames({ active: activeArrowTab === 3 })}
                              onClick={() => toggleArrowTab(3)}
                            >
                              {isJobOwner && !isContractor
                                ? "Hiring"
                                : (isContractor
                                  ? (contractorBidId ? "Edit Bid" : "Submit Bid")
                                  : "Cannot View")}
                            </NavLink>
                          </NavItem>
                        )}
                      </Nav>

                      {/* Tab Content */}
                      <TabContent activeTab={activeArrowTab}>
                        {/* Tab 1: Job Summary */}
                        <TabPane id="steparrow-job-summary" tabId={1}>
                          <div className="job-summary-content">
                            {/* Attachment Gallery - Modern card */}
                            <Card className="modern-card border-0 shadow-sm mb-4">
                              <CardBody className="p-4">
                                <div className="section-header mb-3">
                                  <h5 className="section-title">
                                    <i className="ri-gallery-line me-2"></i>
                                    Project Gallery
                                  </h5>
                                </div>
                                <AttachmentGallery
                                  attachments={jobDetail?.jobAttachments || []}
                                />
                              </CardBody>
                            </Card>

                            <Row className="g-4">
                              {/* Left Column */}
                              <Col lg={6}>
                                {/* Project Address Card */}
                                <Card className="modern-card border-0 shadow-sm mb-4">
                                  <CardBody className="p-4">
                                    <div className="section-header mb-3">
                                      <h5 className="section-title">
                                        <i className="ri-map-pin-line me-2"></i>
                                        Project Location
                                      </h5>
                                    </div>
                                    <div className="address-display">
                                      <p className="address-text mb-0">{formatAddress(jobDetail.propertyAddress)}</p>
                                    </div>
                                  </CardBody>
                                </Card>


                              </Col>

                              {/* Right Column */}
                              <Col lg={6}>
                                {/* Meta Information Card */}
                                <Card className="modern-card border-0 shadow-sm mb-4">
                                  <CardBody className="p-4">
                                    <Row className="g-3">
                                      <Col md={12}>
                                        <div className="info-section">
                                          <h6 className="info-title">
                                            <i className="ri-price-tag-3-line me-2"></i>
                                            Categories
                                          </h6>
                                          <div className="categories-wrapper">
                                            <CategoryBadges categories={getCategories()} />
                                          </div>
                                        </div>
                                      </Col>
                                      <Col md={12}>
                                        <hr className="my-3" />
                                        <div className="info-section">
                                          <h6 className="info-title">
                                            <i className="ri-calendar-line me-2"></i>
                                            Created Date
                                          </h6>
                                          <p className="info-value mb-0">
                                            {formatCreatedDate(jobDetail?.dtCreated)}
                                          </p>
                                        </div>
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              </Col>
                            </Row>


                            <Row className="g-3">
                              <Col md={12}>
                                {/* Job Summary Card */}
                                <Card className="modern-card border-0 shadow-sm mb-4">
                                  <CardBody className="p-4">
                                    <div className="section-header mb-3">
                                      <h5 className="section-title">
                                        <i className="ri-file-text-line me-2"></i>
                                        Project Summary
                                      </h5>
                                    </div>
                                    <div className="content-text">
                                      {renderTextWithLineBreaks(jobDetail?.jobSummaryModified || jobDetail?.jobSummary || '')}
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                              </Row>
                            <Row className="g-3">
                              <Col md={12}>
                                {/* Job Details Card */}
                                <Card className="modern-card border-0 shadow-sm mb-4">
                                  <CardBody className="p-4">
                                    <div className="section-header mb-3">
                                      <h5 className="section-title">
                                        <i className="ri-clipboard-line me-2"></i>
                                        Project Details
                                      </h5>
                                    </div>
                                    <div className="content-text">
                                      {renderTextWithLineBreaks(jobDetail?.jobDetailModified || jobDetail?.jobDetail || '')}
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                              </Row>







                            {/* Property Details Section */}
                            {jobDetail.propertyDetails && (
                              <PropertyDetailsSection propertyDetails={jobDetail.propertyDetails} />
                            )}
                          </div>

                          {/* Navigation button - only show on authenticated routes */}
                          {!isPublicRoute && (isJobOwner || contractor) && (
                            <div className="d-flex align-items-start gap-3 mt-5">
                              <button
                                type="button"
                                className="btn btn-primary btn-lg px-4 py-2 ms-auto modern-btn"
                                onClick={() => toggleArrowTab(activeArrowTab + 1)}
                              >
                                Continue to Discussion
                                <i className="ri-arrow-right-line ms-2"></i>
                              </button>
                            </div>
                          )}
                        </TabPane>

                        {/* Tab 2: Job Changes & Inquiries */}
                        <TabPane id="steparrow-job-changes" tabId={2}>
                          <Row className="g-4">
                            {/* Left Column - Job Information */}
                            <Col lg={6}>
                              {/* Quick Info Panel */}
                              <Card className="modern-card border-0 shadow-sm mb-4">
                                <CardBody className="p-4">
                                  <div className="section-header mb-3">
                                    <h5 className="section-title">
                                      <i className="ri-information-line me-2"></i>
                                      Quick Info
                                    </h5>
                                  </div>

                                  <div className="quick-info-grid">
                                    <div className="info-item">
                                      <span className="info-label">Location:</span>
                                      <span className="info-value">{formatAddress(jobDetail.propertyAddress)}</span>
                                    </div>

                                    <div className="info-item">
                                      <span className="info-label">Categories:</span>
                                      <div className="info-value">
                                        <CategoryBadges categories={getCategories()} />
                                      </div>
                                    </div>

                                    <div className="info-item">
                                      <span className="info-label">Created:</span>
                                      <span className="info-value">{formatCreatedDate(jobDetail?.dtCreated)}</span>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>

                              {/* Original vs Modified Comparison */}
                              <Card className="modern-card border-0 shadow-sm mb-4">
                                <CardBody className="p-4">
                                  <div className="section-header mb-3">
                                    <h5 className="section-title">
                                      <i className="ri-file-compare-line me-2"></i>
                                      Project Evolution
                                    </h5>
                                  </div>

                                  <div className="comparison-tabs">
                                    <Row className="g-3">
                                      <Col md={6}>
                                        <div className="version-panel original">
                                          <h6 className="version-title">Original Summary</h6>
                                          <div className="version-content">
                                            {jobDetail.jobSummary || 'No original summary'}
                                          </div>
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <div className="version-panel modified">
                                          <h6 className="version-title">Current Summary</h6>
                                          <div className="version-content">
                                            <div dangerouslySetInnerHTML={{
                                              __html: getSafeHtmlContent(
                                                jobDetail?.jobSummaryModifiedHTML,
                                                jobDetail?.jobSummaryModified || jobDetail?.jobSummary
                                              )
                                            }} />
                                          </div>
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <div className="version-panel original">
                                          <h6 className="version-title">Original Details</h6>
                                          <div className="version-content">
                                            {jobDetail.jobDetail || 'No original details'}
                                          </div>
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <div className="version-panel modified">
                                          <h6 className="version-title">Current Details</h6>
                                          <div className="version-content">
                                            <div dangerouslySetInnerHTML={{
                                              __html: getSafeHtmlContent(
                                                jobDetail?.jobDetailModifiedHTML,
                                                jobDetail?.jobDetailModified || jobDetail?.jobDetail
                                              )
                                            }} />
                                          </div>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>

                            {/* Right Column - Chat */}
                            <Col lg={6}>
                              <Card className="modern-card border-0 shadow-sm h-100">
                                <CardBody className="p-4 d-flex flex-column">
                                  <div className="section-header mb-3">
                                    <h5 className="section-title">
                                      <i className="ri-chat-3-line me-2"></i>
                                      Project Discussion
                                    </h5>
                                    <p className="section-subtitle text-muted mb-0">Communicate with stakeholders</p>
                                  </div>

                                  <div className="chat-container flex-grow-1">
                                    <div
                                      className="chat-messages modern-chat"
                                      ref={chatContainerRef}
                                    >
                                      {chatMessages.map((msg, index) => (
                                        <ChatMessage
                                          key={msg.id || index}
                                          message={msg.message_text || msg.message || ''}
                                          role={msg.role || 'User'}
                                          timestamp={msg.created_at || msg.timestamp || new Date().toISOString()}
                                          displayName={msg.sender_name || msg.senderName || msg.displayName || 'Unknown'}
                                        />
                                      ))}
                                      {chatMessages.length === 0 && (
                                        <div className="empty-chat-state">
                                          <i className="ri-message-3-line chat-icon"></i>
                                          <p className="empty-message">Start the conversation!</p>
                                        </div>
                                      )}
                                    </div>

                                    <div className="chat-input-area mt-3">
                                      <div className="input-group">
                                        <textarea
                                          className="form-control modern-textarea"
                                          rows={3}
                                          placeholder="Type your message here..."
                                          value={newMessage}
                                          onChange={(e) => setNewMessage(e.target.value)}
                                          onKeyPress={handleMessageKeyPress}
                                        />
                                      </div>
                                      <Button
                                        color="primary"
                                        className="modern-btn mt-2 px-4"
                                        onClick={handleSubmit}
                                        disabled={!newMessage.trim()}
                                      >
                                        <i className="ri-send-plane-line me-2"></i>
                                        Send Message
                                      </Button>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>

                          {/* Navigation */}
                          <div className="d-flex align-items-start gap-3 mt-5">
                            <button
                              type="button"
                              className="btn btn-outline-secondary modern-btn px-4"
                              onClick={() => toggleArrowTab(activeArrowTab - 1)}
                            >
                              <i className="ri-arrow-left-line me-2"></i>
                              Back to Summary
                            </button>
                            {(isJobOwner || isContractor) && (
                              <button
                                type="button"
                                className="btn btn-primary modern-btn px-4 ms-auto"
                                onClick={() => toggleArrowTab(activeArrowTab + 1)}
                              >
                                Continue to {isJobOwner ? "Hiring" : "Bidding"}
                                <i className="ri-arrow-right-line ms-2"></i>
                              </button>
                            )}
                          </div>
                        </TabPane>

                        {/* Tab 3: Bids/Hiring */}
                        <TabPane id="steparrow-job-bids" tabId={3}>
                          <Card className="modern-card border-0 shadow-sm">
                            <CardBody className="p-4">
                              <div className="section-header mb-4">
                                <h5 className="section-title">
                                  <i className="ri-auction-line me-2"></i>
                                  {isJobOwner ? "Manage Bids" : "Submit Your Bid"}
                                </h5>
                                <p className="section-subtitle text-muted mb-0">
                                  {isJobOwner ? "Review and manage contractor bids" : "Provide your proposal for this project"}
                                </p>
                              </div>

                              {isJobOwner && !isContractor ? (
                                <HomeownerBidList
                                  jobId={actualJobId || ''}
                                  jobDetail={jobDetail?.jobDetail || ''}
                                />
                              ) : isContractor ? (
                                <ContractorBidSubmission
                                  jobId={actualJobId || ''}
                                  bidId={contractorBidId || undefined}
                                  onBidSubmit={handleBidSubmit}
                                />
                              ) : (
                                <div className="text-center py-5">
                                  <i className="ri-lock-line display-4 text-muted mb-3"></i>
                                  <h5 className="text-muted">Access Restricted</h5>
                                  <p className="text-muted">You need to be logged in to view this section.</p>
                                </div>
                              )}
                            </CardBody>
                          </Card>

                          {/* Navigation */}
                          <div className="d-flex align-items-start gap-3 mt-5">
                            <button
                              type="button"
                              className="btn btn-outline-secondary modern-btn px-4"
                              onClick={() => toggleArrowTab(activeArrowTab - 1)}
                            >
                              <i className="ri-arrow-left-line me-2"></i>
                              Back to Discussion
                            </button>
                          </div>
                        </TabPane>
                      </TabContent>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Job Edit Modal */}
        {isJobOwner && jobDetail && (
          <JobEdit
            isOpen={isEditModalOpen}
            toggle={handleEditToggle}
            job={jobDetail as Job}
            onSave={handleJobUpdate}
          />
        )}
      </Container>
    </div>
  );
};

export default JobDetailPage;